import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Grid, Tooltip, IconButton, Switch, FormControlLabel, Slider, Select, MenuItem, useTheme } from '@mui/material';
import { usePhotoAddsStore } from '../usePhotoAddsStoreBridge';
import { TEXTURES, textureUrl } from '../constants/textures';
import { applyTextureToSelection, removeTextureFromSelection } from '../services/canvasService';

const TexturesPanel = () => {
  const theme = useTheme();
  const { canvas, updateLayersFromCanvas, selectedLayer } = usePhotoAddsStore();
  const [textureEnabled, setTextureEnabled] = useState(false);
  const [selectedTextureId, setSelectedTextureId] = useState(TEXTURES?.[0]?.id || null);
  const [tintColor, setTintColor] = useState(theme.palette.common.white);
  const [tintOpacity, setTintOpacity] = useState(100); // 0-100
  const [scalePct, setScalePct] = useState(100); // 25-400
  const [rotationDeg, setRotationDeg] = useState(0); // 0-360
  const [repeatMode, setRepeatMode] = useState('repeat'); // repeat | repeat-x | repeat-y | no-repeat

  const buildOptions = useCallback(() => {
    const s = Math.max(1, Math.min(400, scalePct)) / 100; // clamp
    const r = (Math.max(0, Math.min(360, rotationDeg)) * Math.PI) / 180;
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    // 2x3 transform matrix [a, b, c, d, e, f]
    const patternTransform = [s * cos, s * sin, -s * sin, s * cos, 0, 0];
    return {
      repeat: repeatMode,
      patternTransform,
      tintColor: tintColor,
      tintOpacity: Math.max(0, Math.min(100, tintOpacity)),
      textureId: selectedTextureId,
      scalePct,
      rotationDeg,
    };
  }, [scalePct, rotationDeg, repeatMode, tintColor, tintOpacity, selectedTextureId]);

  const applyCurrentTexture = useCallback(async () => {
    if (!canvas || !selectedTextureId) return;
    const tex = TEXTURES.find(t => t.id === selectedTextureId) || TEXTURES[0];
    if (!tex) return;
    await applyTextureToSelection(canvas, textureUrl(tex.file), buildOptions());
    updateLayersFromCanvas?.();
  }, [canvas, selectedTextureId, buildOptions, updateLayersFromCanvas]);

  // This effect will run whenever texture properties change, fixing stale state issues.
  useEffect(() => {
    if (textureEnabled && canvas) {
      applyCurrentTexture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textureEnabled, tintColor, tintOpacity, scalePct, rotationDeg, repeatMode, selectedTextureId, canvas]);


  const handleToggleTexture = async (enabled) => {
    setTextureEnabled(enabled);
    if (!canvas) return;
    if (enabled) {
      // The useEffect will handle applying the texture
    } else {
      removeTextureFromSelection(canvas);
      updateLayersFromCanvas?.();
    }
  };

  const handlePickTexture = (tex) => {
    setSelectedTextureId(tex.id);
    // The useEffect will handle applying the texture
  };

  // Sync controls when switching selected layer
  useEffect(() => {
    const obj = selectedLayer?.object || canvas?.getActiveObject?.();
    if (!obj) return;
    const meta = obj._texture;
    if (meta) {
      if (meta.enabled !== undefined) setTextureEnabled(!!meta.enabled);
      if (meta.textureId) setSelectedTextureId(meta.textureId);
      if (meta.tintColor) setTintColor(meta.tintColor);
      if (meta.tintOpacity !== undefined) setTintOpacity(Math.max(0, Math.min(100, meta.tintOpacity)));
      if (meta.scalePct !== undefined) setScalePct(Math.max(1, Math.min(400, meta.scalePct)));
      if (meta.rotationDeg !== undefined) setRotationDeg(Math.max(0, Math.min(360, meta.rotationDeg)));
      if (meta.repeat) setRepeatMode(meta.repeat);
    } else {
      const isPattern = !!(obj.fill && typeof obj.fill === 'object' && 'source' in obj.fill);
      setTextureEnabled(isPattern);
      // No meta saved: reset to defaults to avoid stale UI values
      setSelectedTextureId(TEXTURES?.[0]?.id || null);
      setTintColor(theme.palette.common.white);
      setTintOpacity(100);
      setScalePct(100);
      setRotationDeg(0);
      setRepeatMode('repeat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayer, canvas]);

  return (
    <Box sx={{ color: theme.palette.text.primary, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Textures</Typography>
      <FormControlLabel
        control={<Switch checked={textureEnabled} onChange={(e) => handleToggleTexture(e.target.checked)} />}
        label={<Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{textureEnabled ? 'On' : 'Off'}</Typography>}
        sx={{ mb: 1 }}
      />

      {/* Controls for Tint, Scale, Rotation */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Tint Color</Typography>
          <input 
            type="color" 
            value={tintColor} 
            onChange={(e) => setTintColor(e.target.value)} 
            disabled={!textureEnabled}
            style={{ width: '100%', height: '30px', border: 'none', borderRadius: '4px', opacity: textureEnabled ? 1 : 0.5 }}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Repeat</Typography>
          <Select 
            size="small" 
            fullWidth
            value={repeatMode} 
            onChange={(e) => setRepeatMode(e.target.value)} 
            sx={{ color: theme.palette.text.primary }}
            disabled={!textureEnabled}
          >
            <MenuItem value="repeat">Repeat</MenuItem>
            <MenuItem value="repeat-x">Repeat-X</MenuItem>
            <MenuItem value="repeat-y">Repeat-Y</MenuItem>
            <MenuItem value="no-repeat">No Repeat</MenuItem>
          </Select>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '100px' }}>Tint Opacity</Typography>
        <Slider 
          min={0} max={100} value={tintOpacity} 
          onChange={(e, v) => setTintOpacity(Array.isArray(v) ? v[0] : v)} 
          onChangeCommitted={() => { /* The useEffect handles this */ }} 
          disabled={!textureEnabled} sx={{ width: 'calc(100% - 140px)', mx: 2 }}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '40px', textAlign: 'right' }}>{tintOpacity}%</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '100px' }}>Scale</Typography>
        <Slider 
          min={25} max={400} value={scalePct} 
          onChange={(e, v) => setScalePct(Array.isArray(v) ? v[0] : v)} 
          onChangeCommitted={() => { /* The useEffect handles this */ }} 
          disabled={!textureEnabled} sx={{ width: 'calc(100% - 140px)', mx: 2 }}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '40px', textAlign: 'right' }}>{scalePct}%</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '100px' }}>Rotation</Typography>
        <Slider 
          min={0} max={360} value={rotationDeg} 
          onChange={(e, v) => setRotationDeg(Array.isArray(v) ? v[0] : v)} 
          onChangeCommitted={() => { /* The useEffect handles this */ }} 
          disabled={!textureEnabled} sx={{ width: 'calc(100% - 140px)', mx: 2 }}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '40px', textAlign: 'right' }}>{rotationDeg}°</Typography>
      </Box>


      {/* Texture grid below, scroll if too tall */}
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <Grid container spacing={1}>
          {TEXTURES.map((t) => (
            <Grid item xs={3} key={t.id}>
              <Tooltip title={t.name}>
                <IconButton
                  onClick={() => handlePickTexture(t)}
                  disabled={!textureEnabled}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    border: selectedTextureId === t.id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.grey[300],
                    backgroundImage: `url(${textureUrl(t.file)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: textureEnabled ? 1 : 0.5,
                  }}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default TexturesPanel;
